import PropTypes from 'prop-types';
import React from 'react';
import { Icon, Table, DatePicker, Typography, Tag } from 'antd';
import moment from 'moment';
import { withTranslation } from 'react-i18next';

import money from '../../../helpers/money';
import { transactionStatuses, transactionTypes } from '../../../helpers/constants';
import { getTransactionFromUri } from '../../../helpers/transaction-utils';
import { LoadingIcon } from '../../../components/loading';

const { Text } = Typography;

const successColor = (success) => ((success) ? 'rgba(0, 0, 0, 0.65)' : '#f81d22');

const formatAmount = ({ amount, currency, status, debit }) => {
  const displayAmount = money.toDisplay(parseInt(amount, 10), currency);
  let color = '#4b998d';
  let lineThrough = false;
  let badge = '';

  if (debit) {
    color = 'rgb(0, 0, 0, 0.65)';
  }

  if (status === transactionStatuses.failed) {
    lineThrough = true;
    color = '#ccc';
    badge = <Tag>canceled</Tag>;
  }

  if (status === transactionStatuses.processing) {
    color = '#ccc';
    badge = <Tag>pending</Tag>;
  }

  return (
    <>
      <Typography.Text style={{ color, textDecoration: (lineThrough) ? 'line-through' : '' }}>
        {(debit) ? '-' : ''}{displayAmount}
      </Typography.Text>
      {badge}
    </>
  );
};

formatAmount.propTypes = {
  amount: PropTypes.string,
  currency: PropTypes.string,
  debit: PropTypes.string,
  status: PropTypes.string,
};

const formatStatus = ({ status, error }, textOnly) => {
  let color = successColor(true);
  let msg = status;
  if (status === transactionStatuses.failed) {
    msg = `${status}: ${error}`;
    color = successColor(false);
  }
  if (textOnly) { return msg; }

  return (
    <Text style={{ color }}>{msg}</Text>
  );
};

formatStatus.propTypes = {
  error: PropTypes.string,
  status: PropTypes.string,
};

const columns = (isStatic, t, type) => {
  if (type === transactionTypes.internal) {
    return [
      {
        title: t('created_at'),
        dataIndex: 'created_at',
        sorter: !isStatic,
        filterIcon: (filtered) => <Icon type="filter" style={{ color: (filtered) ? '#1890ff' : null }} />,
        filterDropdown: (isStatic)
          ? null
          : (({ setSelectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
              <DatePicker.RangePicker onChange={(date) => {
                if (!date.length) {
                  clearFilters();
                } else {
                  setSelectedKeys([date[0].startOf('day'), date[1].endOf('day')]);
                }

                confirm();
              }}
              />
            </div>
          )),
        render: (createdAt) => {
          if (!createdAt || typeof createdAt === 'undefined') {
            return '';
          }

          const diff = moment(createdAt.toDate()).diff(moment(), 'days');
          if (diff >= 0) {
            return moment(createdAt.toDate()).fromNow();
          }

          return moment(createdAt.toDate()).format('lll');
        },
      },
      {
        title: t('amount'),
        dataIndex: 'amount',
        sorter: (a, b) => a.amount - b.amount,
        render: (text, record) => formatAmount(record),
      },
      {
        title: t('name'),
        dataIndex: 'dest_name',
      },
      {
        title: t('description'),
        dataIndex: 'desc',
      },
      {
        title: t('transaction.status'),
        dataIndex: 'status',
        render: (text, record) => formatStatus(record),
      },
    ];
  }

  return [
    {
      title: t('transaction.external.name'),
      dataIndex: 'name',
    },
    {
      title: t('transaction.external.rut'),
      dataIndex: 'ident',
      render: (text, { ident }) => {
        const rut = ident.slice(0, -1);
        const dv = ident.slice(-1);
        const formattedRut = `${rut.slice(0, -6)}.${rut.slice(-6, -3)}.${rut.slice(-3)}-${dv}`.toUpperCase();
        return formattedRut;
      },
    },
    {
      title: t('transaction.external.account_type'),
      dataIndex: 'payment_method',
    },
    {
      title: t('transaction.external.amount'),
      dataIndex: 'amount',
      sorter: (a, b) => a.amount - b.amount,
      render: (text, record) => formatAmount(record),
    },
    {
      title: t('transaction.external.description'),
      dataIndex: 'description',
    },
    {
      title: t('transaction.status'),
      dataIndex: 'status',
      sorter: (a, b) => formatStatus(a, true).localeCompare(formatStatus(b, true)),
      render: (text, record) => formatStatus(record),
    },
  ];
};

const ItemList = withTranslation()((props) => {
  let data = props.transactions;
  if (props.type === 'external') {
    data = data.map(((transaction) => {
      let uriData;
      try {
        uriData = getTransactionFromUri(transaction.dest_uri);
      } catch (error) {
        console.error('ERROR: TransactionList.render: unable to parse transaction data from URI', error); // eslint-disable-line no-console
        return null;
      }

      const nonUri = {
        amount: transaction.amount,
        currency: transaction.currency,
        id: transaction.transaction_id,
        description: transaction.desc,
        status: transaction.status,
        error: transaction.error,
      };

      return { ...uriData, ...nonUri };
    }));
  }

  return (
    <Table
      {...props}
      loading={{
        indicator: LoadingIcon,
        size: 'large',
        spinning: props.loading || false,
      }}
      rowKey={(r, i) => i}
      columns={columns(props.static || false, props.t, props.type)}
      dataSource={data}
      pagination={false}
      size="small"
      scroll={{ x: true }}
    />
  );
});

export { ItemList };
